/**
 * RoSidebarFacets Custom Element
 * Handles the facets sidebar with submenu navigation and filter management
 */
class RoSidebarFacets extends RoSidebarBase {
  // Static property to track last clicked parent index across instances
  static lastParentIndex = null;

  /**
   * Lifecycle: Component connected to DOM
   */
  connectedCallback() {
    this.bindMethods();
    this.initializeComponent();
    this.subscribeToFacetsUpdate();
  }

  /**
   * Bind methods to preserve 'this' context
   */
  bindMethods() {
    this.onMenuClick = this.onMenuClick.bind(this);
    this.onBackClick = this.onBackClick.bind(this);
  }

  /**
   * Initialize the component
   */
  initializeComponent() {
    this.setup();
    this.bindAllEvents();
  }

  /**
   * Subscribe to facets update events
   */
  subscribeToFacetsUpdate() {
    this.facetsUpdateSubscribed = subscribe(PUB_SUB_EVENTS.facetsUpdate, () => {
      this.setup();
      this.bindAllEvents();
    });
  }

  /**
   * Bind all events (both child and parent class events)
   */
  bindAllEvents() {
    this.bindEvents(); // Child class events (menus, back buttons, close buttons)
    super.bindEvents(); // Parent class events (summary, close buttons from base)
  }

  /**
   * Setup the component - re-queries DOM elements and binds events
   */
  setup() {
    super.setup();
    this.queryElements();
    this.bindEvents();
    this.restoreSubmenuState();
    this.resetBackButtonVisibility();
  }

  /**
   * Query all DOM elements needed by this component
   */
  queryElements() {
    // Get the <details> second child (content wrapper)
    this.content = this.el?.children[1];
    
    // Query all interactive elements
    this.closeButtons = this.querySelectorAll('button.btn-close');
    this.menus = this.content?.querySelectorAll('[data-index][id*=Mobile]') || [];
    this.submenus = this.content?.querySelectorAll('.submenu') || [];
    this.backButtons = this.content?.querySelectorAll('.submenu-back') || [];
  }

  /**
   * Restore submenu state if there was a previously clicked parent
   */
  restoreSubmenuState() {
    if (this.dataset.type !== 'filter-sidebar' || !RoSidebarFacets.lastParentIndex) {
      return;
    }

    const lastParentClicked = this.querySelector(
      `a[data-index="${RoSidebarFacets.lastParentIndex}"]`
    );
    
    if (lastParentClicked) {
      const submenu = this.querySelector(
        `.submenu.tw-hidden[data-parent-index="${RoSidebarFacets.lastParentIndex}"]`
      );
      if (submenu) {
        this.showSubmenu(submenu, false);
      }
    }
  }

  /**
   * Reset back button visibility based on active submenus
   */
  resetBackButtonVisibility() {
    if (this.dataset.type !== 'filter-sidebar') {
      return;
    }

    const activeSubmenuIdentifiers = this.querySelectorAll('a.active[id*=Details-Mobile]');
    
    if (activeSubmenuIdentifiers.length === 0) {
      this.backButtons.forEach(backButton => {
        backButton.style.display = null;
      });
    }
  }

  /**
   * Bind child class specific events
   */
  bindEvents() {
    this.attachMenuListeners();
    this.attachBackButtonListeners();
    this.attachCloseButtonListeners();
  }

  /**
   * Attach click listeners to menu items
   */
  attachMenuListeners() {
    this.menus.forEach(menu => {
      menu.addEventListener('click', this.onMenuClick);
    });
  }

  /**
   * Attach click listeners to back buttons
   */
  attachBackButtonListeners() {
    this.backButtons.forEach(backButton => {
      backButton.addEventListener('click', this.onBackClick);
    });
  }

  /**
   * Attach click listeners to close buttons
   */
  attachCloseButtonListeners() {
    this.closeButtons.forEach(closeButton => {
      closeButton.addEventListener('click', super.hide);
    });
  }

  /**
   * Handle menu item click - shows the corresponding submenu
   * @param {Event} event - Click event
   */
  onMenuClick(event) {
    const anchor = event.target.closest('a');
    if (!anchor) return;

    const parentIndex = anchor.dataset.index;
    const targetSubmenuLevel = anchor.dataset.level;
    
    RoSidebarFacets.lastParentIndex = parentIndex;

    const submenuToShow = this.content?.querySelector(
      `.submenu.tw-hidden[data-parent-index="${parentIndex}"]`
    );

    if (!submenuToShow) return;

    this.handleSubmenuNavigation(targetSubmenuLevel, submenuToShow);
  }

  /**
   * Handle submenu navigation logic
   * @param {String} targetLevel - Target submenu level
   * @param {HTMLElement} submenuToShow - Submenu element to show
   */
  handleSubmenuNavigation(targetLevel, submenuToShow) {
    if (targetLevel == 0) {
      // Root level menu item clicked
      this.hideActiveSubmenuAtLevel('1');
    } else {
      // Nested menu item clicked
      const parentIndex = submenuToShow.dataset.parentIndex;
      this.hideActiveSubmenuAtParentIndex(parentIndex, submenuToShow);
    }

    this.showSubmenu(submenuToShow);
  }

  /**
   * Hide active submenu at a specific level
   * @param {String} level - Submenu level
   */
  hideActiveSubmenuAtLevel(level) {
    const activeSubmenu = this.content?.querySelector(
      `.submenu:not(.tw-hidden)[data-level="${level}"]`
    );
    
    if (activeSubmenu) {
      this.hideSubmenu(activeSubmenu);
    }
  }

  /**
   * Hide active submenu at a specific parent index
   * @param {String} parentIndex - Parent index
   * @param {HTMLElement} submenuToShow - Submenu to show (won't be hidden)
   */
  hideActiveSubmenuAtParentIndex(parentIndex, submenuToShow) {
    const activeSubmenu = this.content?.querySelector(
      `.submenu:not(.tw-hidden)[data-parent-index="${parentIndex}"]`
    );

    if (activeSubmenu && activeSubmenu !== submenuToShow) {
      this.hideSubmenu(activeSubmenu);
    }
  }

  /**
   * Handle back button click - hides the active submenu
   * @param {Event} event - Click event
   */
  onBackClick(event) {
    const activeSubmenu = this.content?.querySelector('.submenu:not(.tw-hidden)');

    if (activeSubmenu) {
      this.hideSubmenu(activeSubmenu);
      RoSidebarFacets.lastParentIndex = null;
    }
  }

  /**
   * Show a submenu with optional animation
   * @param {HTMLElement} submenu - Submenu element to show
   * @param {Boolean} animate - Whether to animate the transition
   */
  showSubmenu(submenu, animate = true) {
    if (!submenu) return;

    const parentIndex = submenu.dataset.parentIndex;
    const parentLink = this.content?.querySelector(`a[data-index="${parentIndex}"]`);
    
    if (parentLink) {
      parentLink.classList.add('active');
    }

    submenu.classList.remove('tw-hidden');

    if (animate) {
      requestAnimationFrame(() => {
        this.applySubmenuShowClasses(submenu);
      });
    } else {
      this.applySubmenuShowClasses(submenu);
    }

    this.showBackButtons();
  }

  /**
   * Apply CSS classes to show submenu
   * @param {HTMLElement} submenu - Submenu element
   */
  applySubmenuShowClasses(submenu) {
    submenu.classList.add('tw-opacity-100', 'tw-translate-x-0');
    submenu.classList.remove('tw-opacity-0', '-tw-translate-x-full');
  }

  /**
   * Show all back buttons
   */
  showBackButtons() {
    this.backButtons.forEach(backButton => {
      backButton.style.display = 'flex';
    });
  }

  /**
   * Hide a submenu with animation
   * @param {HTMLElement} submenu - Submenu element to hide
   */
  hideSubmenu(submenu) {
    if (!submenu) return;

    const parentIndex = submenu.dataset.parentIndex;
    const parentLink = this.content?.querySelector(`a[data-index="${parentIndex}"]`);
    
    if (parentLink) {
      parentLink.classList.remove('active');
    }

    this.applySubmenuHideClasses(submenu);
    this.hideBackButtons();

    // Add hidden class after transition completes
    submenu.addEventListener('transitionend', () => {
      submenu.classList.add('tw-hidden');
    }, { once: true });
  }

  /**
   * Apply CSS classes to hide submenu
   * @param {HTMLElement} submenu - Submenu element
   */
  applySubmenuHideClasses(submenu) {
    submenu.classList.remove('tw-opacity-100', 'tw-translate-x-0');
    submenu.classList.add('tw-opacity-0', '-tw-translate-x-full');
  }

  /**
   * Hide all back buttons
   */
  hideBackButtons() {
    this.backButtons.forEach(backButton => {
      backButton.style.display = null;
    });
  }

  /**
   * Override animation method to use vertical translation (bottom-up)
   * @param {Boolean} opening - Whether sidebar is opening
   */
  animateContent(opening) {
    const ANIMATION_DURATION = 300;
    const openState = 'translateY(0)';
    const hiddenState = 'translateY(100%)';
    const startTranslate = opening ? hiddenState : openState;
    const endTranslate = opening ? openState : hiddenState;

    // Cancel any existing animation
    if (this.animation) {
      this.animation.cancel();
    }

    // Create new animation
    this.animation = this.content.animate({
      transform: [startTranslate, endTranslate],
      opacity: [opening ? 0 : 1, opening ? 1 : 0]
    }, {
      duration: ANIMATION_DURATION,
      easing: 'ease-out'
    });

    this.animation.onfinish = () => this.onAnimationFinish(opening);
    this.animation.oncancel = () => {
      this.isClosing = false;
      this.isExpanding = false;
    };
  }
}

customElements.define('ro-sidebar-facets', RoSidebarFacets);
